import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getBlueprints, createSubscriptionCheckout } from '../services/api';
import { useCustomerAuth } from '../services/CustomerAuthContext';
import { useAuth } from '../services/AuthContext';

function WorkshopBrowse() {
  const [blueprints, setBlueprints] = useState([]);
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const { customer } = useCustomerAuth();
  const { vendor } = useAuth();
  const navigate = useNavigate();

  const tier = customer?.subscriptionTier || '';
  const isVendor = vendor?.vendorStatus === 'active';
  const hasAccess = isVendor || tier === 'insider';

  useEffect(() => {
    if (hasAccess) {
      getBlueprints().then(data => setBlueprints(data.blueprints || [])).catch(() => {});
    }
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="workshop-page">
        <Header />
        <div className="workshop-content">
          <h1 className="workshop-title">📐 The Workshop</h1>
          <p className="workshop-subtitle">Share, fork, and remix blueprints across every craft</p>

          {/* Sneak Peek — Provenance Graph Demo */}
          <div className="workshop-preview-section">
            <h2 style={{marginBottom:'0.5rem'}}>How it works</h2>
            <p style={{color:'var(--text-muted)', marginBottom:'1.5rem', fontSize:'0.9rem'}}>Every blueprint has a living Provenance Graph — fork any plan, make it yours, and watch the community grow.</p>
            <div className="workshop-preview-graph">
              <svg width="320" height="280">
                <line x1="15" y1="15" x2="15" y2="200" stroke="#c0392b" strokeWidth="3"/>
                <circle cx="15" cy="30" r="7" fill="#c0392b"/>
                <circle cx="15" cy="100" r="7" fill="#c0392b"/>
                <circle cx="15" cy="170" r="7" fill="#c0392b"/>
                <path d="M 15 30 C 15 60, 60 55, 60 80" stroke="#4a7c59" strokeWidth="2.5" fill="none" opacity="0.8"/>
                <circle cx="60" cy="80" r="6" fill="#4a7c59"/>
                <path d="M 15 100 C 15 140, 110 130, 110 160" stroke="#5b8dd9" strokeWidth="2.5" fill="none" opacity="0.8"/>
                <circle cx="110" cy="160" r="6" fill="#5b8dd9"/>
                <path d="M 60 80 C 60 120, 160 110, 160 140" stroke="#ab47bc" strokeWidth="2.5" fill="none" opacity="0.8"/>
                <circle cx="160" cy="140" r="6" fill="#ab47bc"/>
                <path d="M 15 170 C 15 210, 80 210, 80 240" stroke="#e67e22" strokeWidth="2.5" fill="none" opacity="0.8"/>
                <circle cx="80" cy="240" r="6" fill="#e67e22"/>
                {/* Labels */}
                <text x="28" y="34" fill="var(--text)" fontSize="11" fontWeight="600">v1.0 — Original</text>
                <text x="28" y="104" fill="var(--text)" fontSize="11" fontWeight="600">v1.1 — Revised</text>
                <text x="28" y="174" fill="var(--text)" fontSize="11" fontWeight="600">v2.0 — Major update</text>
                <text x="72" y="84" fill="#4a7c59" fontSize="10">Sarah's take</text>
                <text x="122" y="164" fill="#5b8dd9" fontSize="10">Jay's remix</text>
                <text x="172" y="144" fill="#ab47bc" fontSize="10">Fork of Sarah's</text>
                <text x="92" y="244" fill="#e67e22" fontSize="10">New contributor</text>
              </svg>
            </div>
          </div>

          {/* Feature highlights */}
          <div className="workshop-preview-features">
            <div className="preview-feature">
              <span className="preview-feature-icon">✂️</span>
              <h4>Fork Any Plan</h4>
              <p>Make your own version of any blueprint. Attribution is permanent — credit always flows back.</p>
            </div>
            <div className="preview-feature">
              <span className="preview-feature-icon">📐</span>
              <h4>Full Build Plans</h4>
              <p>Materials lists, step-by-step instructions, tools required, and attached files (STL, PDF, SVG).</p>
            </div>
            <div className="preview-feature">
              <span className="preview-feature-icon">🔨</span>
              <h4>Shop Results</h4>
              <p>See what others built from the same plan. Post your own results and compare approaches.</p>
            </div>
            <div className="preview-feature">
              <span className="preview-feature-icon">🌳</span>
              <h4>Provenance Graph</h4>
              <p>Every fork, every revision — visualized. Click through the tree to explore how ideas evolve.</p>
            </div>
          </div>

          {/* Paywall CTA */}
          <div className="workshop-paywall-cta">
            <h2>🔒 Insider Access Required</h2>
            <p style={{color:'var(--text-muted)', margin:'0.5rem 0 1.5rem'}}>Unlock the full Workshop — browse, fork, and publish blueprints across every craft.</p>
            {customer ? (
              <button className="btn-primary" onClick={async () => {
                try { const data = await createSubscriptionCheckout('insider'); window.location.href = data.url; }
                catch (e) { alert(e.error || 'Failed to start checkout'); }
              }}>Upgrade to Insider — $12.99/mo</button>
            ) : (
              <div style={{display:'flex', gap:'1rem', justifyContent:'center'}}>
                <Link to="/customer/login" className="btn-primary" style={{textDecoration:'none'}}>Sign In</Link>
                <Link to="/customer/register" className="btn-secondary" style={{textDecoration:'none'}}>View Plans</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const filtered = blueprints.filter(bp => {
    const matchSearch = !search || bp.title.toLowerCase().includes(search.toLowerCase()) || (bp.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchDiff = !difficulty || bp.difficulty === difficulty;
    return matchSearch && matchDiff;
  });

  return (
    <div className="workshop-page">
      <Header />
      <div className="workshop-content">
        <h1 className="workshop-title">📐 The Workshop</h1>
        <p className="workshop-subtitle">Share, build, and remix woodworking blueprints</p>

        <div className="workshop-filters">
          <input type="text" className="workshop-search" placeholder="Search blueprints, techniques, species..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="workshop-filter-select" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <div className="blueprints-grid">
          {filtered.length === 0 && <p className="empty-state">No blueprints yet. Be the first to publish one!</p>}
          {filtered.map(bp => (
            <Link to={`/workshop/blueprints/${bp.blueprintId}`} className="bp-card-link" key={bp.blueprintId}>
              <div className="bp-card">
                <div className="bp-card-top">
                  <div>
                    <h3>{bp.title}</h3>
                    <p className="bp-author">by {bp.creatorName || bp.creatorId}</p>
                  </div>
                  {bp.price > 0 ? (
                    <span className="bp-price">${(bp.price / 100).toFixed(2)}</span>
                  ) : (
                    <span className="bp-free">Free</span>
                  )}
                </div>
                <div className="bp-meta">
                  <span>⏱ ~{bp.estimatedHours}hr</span>
                  <span>📏 {bp.difficulty}</span>
                  <span>v{bp.currentVersion}</span>
                </div>
                <div className="bp-tags">
                  {(bp.tags || []).map(t => <span className="bp-tag" key={t}>{t}</span>)}
                </div>
                <div className="bp-stats">
                  <span>★ {bp.starCount || 0}</span>
                  <span>🔀 {bp.variationCount || 0}</span>
                  <span>🔨 {bp.shopResultCount || 0}</span>
                </div>
                <span className={`bp-license-badge ${bp.license}`}>{bp.license === 'mit' ? '🟢 Open' : bp.license === 'cc-by' ? '🟡 Attribution' : '🔒 Protected'}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WorkshopBrowse;
